import React from 'react';
import { compareMetricToIndustryStandard } from 'common/utils/utils';
import { getDefaultFormatter } from 'common/utils/time.utils';
import { getDurationLevels } from '@linearb/linui-utils';

import Text from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import _ from 'lodash';
import { metricNames } from '@linearb/linjs-common';
import {
  CycleTimePopupTooltip,
  PMCodingTimePopupTooltip
} from 'components/Tooltips/CycleTimeTooltip';
import { Circle } from 'components/Shapes';
import { getCycleTimeConfigurations } from '../../../dashboard/dashboard.service';
import { mapCycleTimeStatisticsToPipelineMilestones } from 'components/DeliveryPipeline';
import Icon from 'components/base/Icon';
import { Tooltip } from '@material-ui/core';
import { WeekendWorkTooltip } from './tooltips';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';

const willBeExcludedText =
  'It will be excluded from your aggregated cycle time.';

const getCodingTimeProviderKey = (extra = {}) =>
  _.get(extra, 'coding_time_issue.provider_key', null);

const cycleTimeTypes = {
  WIP: 'WIP',
  EXCLUDED_OUTLIER: 'EXCLUDED_OUTLIER',
  EXCLUDED_BY_USER: 'EXCLUDED_BY_USER',
  EXCLUDED_ON_NO_TICKET: 'EXCLUDED_ON_NO_TICKET',
  REGULAR: 'REGULAR'
};

export const cycleTimeExclusionReasons = {
  DETECTED_OUTLIER: 'detected-outlier',
  NO_TICKET_ATTACHED: 'no-ticket-attached',
  USER_SELECTION: 'user-selection'
};

export const typeToConfig = {
  WIP: {
    color: 'dodgerBlue',
    title: 'Cycle time breakdown - In progress',
    description:
      'This item is still in progress, its final cycle time will be updated when complete.'
  },
  EXCLUDED_OUTLIER: {
    color: 'torchRed',
    title: 'Cycle time breakdown - Outlier detected*',
    description: `This item was flagged as an outlier based on multiple attributes. ${willBeExcludedText}`
  },
  EXCLUDED_ON_NO_TICKET: {
    color: 'torchRed',
    title: 'Cycle time breakdown - No ticket detected*',
    description: `We couldn't find an existing ticket for this item. ${willBeExcludedText}`
  },
  EXCLUDED_BY_USER: {
    color: 'orangePeel',
    title: 'Cycle time breakdown - Excluded manually*',
    description: `This item was excluded manually. ${willBeExcludedText}`
  },
  REGULAR: {
    color: 'pastelGreen',
    title: 'Cycle time breakdown',
    description:
      'The work on this item has completed, it is either merged or released.'
  }
};

const WeekendWorkLabel = () => {
  return (
    <Flex
      borderRadius='30px'
      backgroundColor='#9077F6'
      width='12rem'
      height='20px'
      items='center'
      justify='center'
    >
      <Text weight='bold' color='white' size='medium'>
        Includes Weekends
      </Text>
    </Flex>
  );
};

export const isExcludeReasonDetectedOutlier = (
  isCycleTimeExcluded,
  cycleTimeExclusionReason
) => {
  return (
    isCycleTimeExcluded &&
    cycleTimeExclusionReason === cycleTimeExclusionReasons.DETECTED_OUTLIER
  );
};
export const isExcludeReasonUserSelection = (
  isCycleTimeExcluded,
  cycleTimeExclusionReason
) => {
  return (
    isCycleTimeExcluded &&
    cycleTimeExclusionReason === cycleTimeExclusionReasons.USER_SELECTION
  );
};
export const isExcludeReasonNoTicketAttached = (
  isCycleTimeExcluded,
  cycleTimeExclusionReason
) => {
  return (
    isCycleTimeExcluded &&
    cycleTimeExclusionReason === cycleTimeExclusionReasons.NO_TICKET_ATTACHED
  );
};

export const getCycleTimeType = ({
  cycle_time_excluded: isCycleTimeExcluded,
  cycle_time_excluded_reason: cycleTimeExclusionReason,
  stats: {
    [metricNames.REVIEW_TIME]: timeToMerged,
    [metricNames.TIME_TO_PROD]: timeToProd
  } = {}
} = {}) => {
  switch (true) {
    case isExcludeReasonDetectedOutlier(
      isCycleTimeExcluded,
      cycleTimeExclusionReason
    ):
      return cycleTimeTypes.EXCLUDED_OUTLIER;
    case isExcludeReasonUserSelection(
      isCycleTimeExcluded,
      cycleTimeExclusionReason
    ):
      return cycleTimeTypes.EXCLUDED_BY_USER;
    case isExcludeReasonNoTicketAttached(
      isCycleTimeExcluded,
      cycleTimeExclusionReason
    ):
      return cycleTimeTypes.EXCLUDED_ON_NO_TICKET;
    case !_.isNumber(timeToMerged) && !_.isNumber(timeToProd):
      return cycleTimeTypes.WIP;
    default:
      return cycleTimeTypes.REGULAR;
  }
};

const BottomLine = styled(Flex)`
  width: 100%;
  height: 2px;
  ${({ color }) =>
    color ? `border-bottom: 2px solid ${getColor(color)};` : ''}
`;

export const getAccountedCycleTime = (includedStages, stats) => {
  return includedStages.reduce((sum, metricStage) => {
    return sum + _.get(stats, metricStage, 0);
  }, 0);
};

export const CycleTime = ({
  subtitle = null,
  showAccountedTab = false,
  included_stages: includedStages = null,
  branchData: {
    cycle_time: cycleTimeStatsInMinutes = null,
    cycle_time_excluded,
    cycle_time_excluded_reason,
    stats,
    metrics_included_weekends
  } = {},
  isHideNonWorkingDaysEnabled = false,
  isHideMetricsCalculationConfig = false
}) => {
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const metricsIncludedWeekends = metrics_included_weekends || {};
  const isIncludeWeekends = Object.values(metricsIncludedWeekends).some(
    val => val
  );
  if (cycleTimeStatsInMinutes === null) {
    return <Text size='medium'>N/A</Text>;
  }
  const { color, title, description } =
    typeToConfig[
      getCycleTimeType({
        cycle_time_excluded,
        cycle_time_excluded_reason,
        stats,
        includedStages
      })
    ];

  const accountedCycleTimeInMinutes =
    includedStages?.length && showAccountedTab
      ? getAccountedCycleTime(includedStages, stats)
      : null;

  return (
    <CycleTimePopupTooltip
      stats={stats}
      includedStages={includedStages}
      showAccountedTab={showAccountedTab}
      subtitle={subtitle}
      title={
        <Flex items='center'>
          <Circle size='0.8' color={color} />
          <Text pl='0.8rem' weight='500' size='x-large'>
            {title}
          </Text>
        </Flex>
      }
      extraInfo={description}
      scale={0.9}
      configurations={getCycleTimeConfigurations(
        mapCycleTimeStatisticsToPipelineMilestones(
          stats,
          {},
          metricsCalculationConfig,
          benchmarkOrgSize
        ),
        true,
        {},
        metricsIncludedWeekends
      )}
      totalTime={getDurationLevels({
        minutes: cycleTimeStatsInMinutes,
        ignoreZeros: true,
        format: getDefaultFormatter({
          minutes: cycleTimeStatsInMinutes,
          hoursUnit: ' hours',
          daysUnit: ' days',
          minutesUnit: ' minutes',
          yearsUnit: ' years'
        }),
        joiner: ' '
      })}
      accountedCycleTime={getDurationLevels({
        minutes: accountedCycleTimeInMinutes,
        ignoreZeros: true,
        format: getDefaultFormatter({
          minutes: accountedCycleTimeInMinutes,
          hoursUnit: ' hours',
          daysUnit: ' days',
          minutesUnit: ' minutes',
          yearsUnit: ' years'
        }),
        joiner: ' '
      })}
      metricsCalculationConfig={metricsCalculationConfig}
      isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
      isHideMetricsCalculationConfig={isHideMetricsCalculationConfig}
    >
      <Flex direction='column'>
        <Flex items='center' pb='small'>
          <Circle size='0.8' color={color} />
          <Text pl='0.3rem' size='medium'>
            {getDurationLevels({
              minutes: cycleTimeStatsInMinutes,
              shortDisplay: { maxDays: 10, maxHours: 20 },
              format: getDefaultFormatter({ minutes: cycleTimeStatsInMinutes })
            })}
          </Text>
        </Flex>
        {isIncludeWeekends && isHideNonWorkingDaysEnabled && (
          <WeekendWorkLabel />
        )}
      </Flex>
    </CycleTimePopupTooltip>
  );
};

export const CycleTimePipe = props => {
  const {
    branchData,
    location,
    extra,
    isIncludeWeekends = false,
    isHideNonWorkingDaysEnabled
  } = props;
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const codingTimeProviderKey = getCodingTimeProviderKey(extra);
  const { stats } = branchData;
  const value = stats[location];
  if (value === null || value === undefined) {
    return <Text size='medium'>N/A</Text>;
  }

  const { color } = compareMetricToIndustryStandard(
    location,
    _.isNumber(value) ? value : null,
    metricsCalculationStrategy,
    benchmarkOrgSize
  );

  const duration = getDurationLevels({
    minutes: value,
    format: getDefaultFormatter({ minutes: value }),
    shortDisplay: { maxDays: 10, maxHours: 20 }
  });

  const CodingTimeTooltip = ({ children }) => (
    <PMCodingTimePopupTooltip
      title='Coding time'
      duration={duration}
      codingTimeProviderKey={codingTimeProviderKey}
    >
      {children}
    </PMCodingTimePopupTooltip>
  );

  const component = (
    <Flex
      items='flex-start'
      direction='column'
      style={{ marginTop: `calc(0.5rem + 2px)`, width: '8rem' }}
    >
      <Flex>
        <Text pl='0.3rem' size='medium' align='left' pb='0.5rem'>
          {duration}
        </Text>
        {isIncludeWeekends && isHideNonWorkingDaysEnabled && (
          <Tooltip title={<WeekendWorkTooltip />}>
            <Padding l='small'>
              <Icon color='dodgerBlue' name='infoOutlined' />
            </Padding>
          </Tooltip>
        )}
      </Flex>
      <BottomLine align='flex-start' color={color} />
    </Flex>
  );
  return codingTimeProviderKey ? (
    <CodingTimeTooltip>{component}</CodingTimeTooltip>
  ) : (
    component
  );
};
